import {
  getScaleFromIncrement,
  toBig,
  toBigWithDefault,
  useCurrency,
  useSecurity,
  type CustomerQuote,
  type Quote,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { roundPxOrAmtInMyFavor } from '../getPricingDetailsFromFixing';
import type { ModifyFixingEntry } from './modifyFixingSlice';

interface UseTopLevelComputedValuesParams {
  entries: ModifyFixingEntry[];
  topLevelAmount: string | undefined;
  quote: Quote | CustomerQuote;
}

/**
 * This hook computes the values that are implied given the entries (blotter rows)
 * It sums the entries' amounts, and then divides by the top-level quantity to get a top-level price
 */
export function useTopLevelComputedValues({ entries, topLevelAmount, quote }: UseTopLevelComputedValuesParams) {
  const security = useSecurity(quote.Symbol);
  const amountCurrency = useCurrency(quote.AmountCurrency);

  return useMemo(() => {
    if (!quote.TradedSide) {
      return { computedPrice: undefined, summedLegTotal: undefined };
    }

    const amountScale = getScaleFromIncrement(amountCurrency?.MinIncrement);

    let hasInvalidLegAmount = false;
    let summedLegTotalUnrounded = toBigWithDefault('0', 0);
    for (const entry of entries) {
      const entryAmount = entry.amountField.bigValue;
      if (!entryAmount) {
        hasInvalidLegAmount = true;
        break;
      }
      summedLegTotalUnrounded = summedLegTotalUnrounded.plus(entryAmount);
    }

    const summedLegTotal = hasInvalidLegAmount
      ? undefined
      : roundPxOrAmtInMyFavor(summedLegTotalUnrounded, amountScale, quote.TradedSide);

    const topLevelAmountBig = toBig(topLevelAmount);
    const tradedQty = toBig(quote.TradedQty);
    if (!topLevelAmountBig || !tradedQty || tradedQty.eq(0)) {
      return { computedPrice: undefined, summedLegTotal };
    }

    const priceScale = getScaleFromIncrement(security?.MinPriceIncrement);
    const computedPrice = roundPxOrAmtInMyFavor(topLevelAmountBig.div(tradedQty), priceScale, quote.TradedSide);

    return { computedPrice, summedLegTotal };
  }, [
    amountCurrency?.MinIncrement,
    entries,
    quote.TradedQty,
    quote.TradedSide,
    security?.MinPriceIncrement,
    topLevelAmount,
  ]);
}
